import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * One recommended product in the cart drawer, sourced from Shopify's own
 * product-recommendations JSON endpoint (the same algorithm/data source the
 * rest of the theme uses -- see sections/product-recommendations.liquid).
 *
 * This is a small, purpose-built component rather than a reuse of the
 * existing <product-recommendations> element: that element's Section
 * Rendering API contract requires a section_id that already exists as a
 * real, template-placed section instance on the current page, which a
 * cart-drawer-only recommendation (whose target product changes on every
 * add, independent of what page the customer is on) cannot provide. Using
 * the JSON data endpoint directly avoids that mismatch entirely while still
 * using Shopify's real recommendation engine, not invented data.
 *
 * Lazy-loaded on the drawer's own DrawerOpenEvent (the same event
 * cart-drawer.js listens to for its sticky-summary/installments wiring),
 * not an IntersectionObserver: the element sits inside a closed <dialog>
 * with zero rendered height until content loads, which is not a reliable
 * IntersectionObserver target in every browser. Listening for the drawer's
 * own open event is both simpler and guarantees "never fetch before the
 * customer actually opens the drawer."
 *
 * Adding the recommended product reuses the theme's own /cart/add.js
 * endpoint and dispatches the same CartLinesUpdateEvent contract
 * assets/product-form.js uses, so cart-items-component (assets/component-
 * cart-items.js) morphs the drawer exactly as it would for any other add --
 * no duplicate cart-state/morph logic.
 */
class CartDrawerRecommendation extends HTMLElement {
  #loaded = false;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  connectedCallback() {
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    // The drawer may already be open (e.g. this element was just morphed in
    // after an add-to-cart while the drawer was already showing).
    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  #handleDrawerOpen = () => {
    if (this.#loaded) return;
    this.#loaded = true;
    this.#load();
  };

  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    event.promise
      ?.then(({ detail }) => {
        const productId = detail?.productId;
        if (!productId || String(productId) === this.dataset.productId) return;
        this.dataset.productId = String(productId);
        this.#load();
      })
      .catch(() => {
        // A failed add already surfaces its own error via CartErrorEvent; nothing to do here.
      });
  };

  async #load() {
    const productId = this.dataset.productId;
    const url = this.dataset.url;
    if (!productId || !url) return;

    const excludeIds = (this.dataset.excludeIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    try {
      const response = await fetch(`${url}&product_id=${productId}`);
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      const product = products.find((candidate) => !excludeIds.includes(String(candidate.id))) || products[0];

      if (!product) {
        this.innerHTML = '';
        return;
      }

      this.#render(product);
    } catch (error) {
      console.error('Cart drawer recommendation error:', error);
      this.innerHTML = '';
    }
  }

  /**
   * @param {{id: number, title: string, url: string, images: string[], price: number, variants: Array<{id: number, available: boolean}>}} product
   */
  #render(product) {
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const image = product.images?.[0];
    const priceFormatted = this.#formatMoney(product.price);

    const titleText = this.dataset.titleText || 'You may also like';
    const addText = this.dataset.addText || 'Add';

    this.innerHTML = `
      <p class="cart-drawer-recommendation__eyebrow">${titleText}</p>
      <div class="cart-drawer-recommendation__card">
        <a href="${product.url}" class="cart-drawer-recommendation__media" tabindex="-1" aria-hidden="true">
          ${image ? `<img src="${image}" width="48" height="48" loading="lazy" alt="">` : ''}
        </a>
        <div class="cart-drawer-recommendation__info">
          <a href="${product.url}" class="cart-drawer-recommendation__title">${product.title}</a>
          <span class="cart-drawer-recommendation__price">${priceFormatted}</span>
        </div>
        <button type="button" class="cart-drawer-recommendation__add" ${variant?.available ? '' : 'disabled'}>
          ${addText}
        </button>
      </div>
    `;

    const addButton = this.querySelector('.cart-drawer-recommendation__add');
    addButton?.addEventListener('click', () => this.#addToCart(variant.id, product.id), { once: true });
  }

  /**
   * Minimal Shopify money-format renderer, using the store's own format
   * string (passed from Liquid as shop.money_format -- no hardcoded
   * currency symbol or global money-formatting helper assumed to exist).
   * @param {number} cents
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = this.dataset.moneyFormat || '₹{{amount}}';
    const withThousands = Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace('{{amount}}', withThousands).replace('{{ amount }}', withThousands);
  }

  /**
   * @param {number} variantId
   * @param {number} productId
   */
  async #addToCart(variantId, productId) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItemsComponents]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-drawer-recommendation',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionIds.join(',') }),
      });
      const addResult = await addResponse.json();

      if (addResult.status) {
        const error = new Error(addResult.message || 'Add to cart failed');
        document.dispatchEvent(
          new CartErrorEvent({ error: error.message, code: 'INVALID', detail: { description: addResult.description } })
        );
        deferredEventPromise.reject(error);
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const ajaxCart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-drawer-recommendation',
          sourceId: 'cart-drawer-recommendation',
          itemCount: 1,
          productId,
          sections: addResult.sections,
          didError: false,
        },
      });
    } catch (error) {
      document.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      deferredEventPromise.reject(error);
    }
  }
}

if (!customElements.get('cart-drawer-recommendation')) {
  customElements.define('cart-drawer-recommendation', CartDrawerRecommendation);
}
